import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { getMyClubJoinRequests } from '../../services/clubJoinRequestService';
import { Card } from '../common/Card';
import { Text } from '../common/Typography';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';

/**
 * PendingJoinRequestsCard - Shows the user's pending and rejected club join requests
 * Allows users to withdraw requests and view club details
 */
const PendingJoinRequestsCard = ({ currentUser, dark = false }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [withdrawingRequest, setWithdrawingRequest] = useState(null);

  const loadPendingRequests = useCallback(async () => {
      if (!currentUser?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const joinRequestsData = (await getMyClubJoinRequests()).filter(
          (request) => request.status === 'pending' || request.status === 'rejected'
        );

        if (!joinRequestsData || joinRequestsData.length === 0) {
          setRequests([]);
          setLoading(false);
          return;
        }

        // Fetch club details for each request
        const clubIds = [...new Set(joinRequestsData.map(r => r.club_id))];
        const { data: clubsData, error: clubsError } = await supabase
          .from('clubs')
          .select('id, name, location')
          .in('id', clubIds);

        if (clubsError) throw clubsError;

        // Combine request and club data
        const requestsWithClubs = joinRequestsData.map(request => {
          const club = clubsData.find(c => c.id === request.club_id);
          return {
            ...request,
            club: club || { id: request.club_id, name: 'Unknown Club', location: null }
          };
        });

        // Sort by created_at (most recent first)
        requestsWithClubs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setRequests(requestsWithClubs);
      } catch (err) {
        console.error('Error loading pending join requests:', err);
        setError('Failed to load pending requests');
      } finally {
        setLoading(false);
      }
  }, [currentUser?.id]);

  useEffect(() => {
    loadPendingRequests();
  }, [loadPendingRequests]);

  const handleWithdrawRequest = async (requestId, clubId) => {
    if (!requestId) {
      setError('Unable to withdraw request.');
      return;
    }

    try {
      setWithdrawingRequest(requestId);
      setError(null);

      const { data, error: withdrawError } = await supabase
        .rpc('withdraw_join_request', {
          p_request_id: requestId,
          p_player_id: currentUser.id
        });

      if (withdrawError) throw withdrawError;

      if (data?.error) {
        throw new Error(data.error || 'Failed to withdraw request');
      }

      // Remove the withdrawn request from the list
      setRequests(prev => prev.filter(r => r.id !== requestId));

      await loadPendingRequests();
    } catch (err) {
      console.error('Error withdrawing join request:', err);
      setError(err.message || 'Failed to withdraw request.');
    } finally {
      setWithdrawingRequest(null);
    }
  };

  const handleViewClub = (clubId) => {
    navigate(`/clubs`);
    // Scroll to the club card or open club info modal
    // For now, just navigate to clubs page
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[date.getDay()];
    const dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${dayName}, ${dateStr}`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm animate-pulse bg-surface-subtle border border-border">
        <div className="w-4 h-4 rounded-full bg-border-medium" />
        <div className="h-3 w-32 rounded bg-border-medium" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm bg-surface-subtle border border-border text-text-muted">
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>No pending club requests</span>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <Text className="text-sm text-red-600">{error}</Text>
        </div>
      )}

      <div className="space-y-2">
          {requests.map((request) => {
          const isPending = request.status === 'pending';
          const isRejected = request.status === 'rejected';

          return (
            <div
              key={request.id}
              className="p-4 border border-border rounded-xl bg-surface-subtle"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left side - Club info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-text-primary">
                      {request.club.name}
                    </div>
                    <StatusBadge 
                      variant={isPending ? 'info' : 'danger'} 
                      size="small"
                    >
                      {isPending ? 'Pending' : 'Rejected'}
                    </StatusBadge>
                  </div>
                  {request.club.location && (
                    <div className="text-sm text-text-secondary mb-1">
                      {request.club.location}
                    </div>
                  )}
                  {request.created_at && (
                    <div className="text-xs text-text-muted">
                      Requested: {formatDate(request.created_at)}
                    </div>
                  )}
                  {isRejected && request.rejection_message && (
                    <div className="mt-2 text-xs bg-surface border border-border p-2 rounded text-text-secondary">
                      <strong>Message:</strong> {request.rejection_message}
                    </div>
                  )}
                </div>

                {/* Right side - Actions */}
                <div className="flex-shrink-0 flex flex-col gap-2">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => handleViewClub(request.club.id)}
                  >
                    View Club
                  </Button>
                  {(isPending || isRejected) && (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => handleWithdrawRequest(request.id, request.club.id)}
                      disabled={withdrawingRequest === request.id}
                    >
                      {withdrawingRequest === request.id ? 'Withdrawing...' : 'Withdraw Request'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
          })}
        </div>
    </div>
  );
};

export default PendingJoinRequestsCard;

